import React from 'react';
import { ShieldCheck, Users, Laptop, Server, Globe2, AlertTriangle, Cloud, MapPinned } from 'lucide-react';
import Gauge, { scoreLabel } from './Gauge';

const C = { ok: '#00FF87', info: '#00F0FF', warn: '#FFB800', bad: '#FF2E4D', off: '#5F6E82' };
const NIS = { essential: 'bad', important: 'warn', supply_chain: 'warn', out: 'ok' };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function Bar({ ratio, color }) {
  const on = Math.round(Math.max(0, Math.min(1, ratio)) * 12);
  return <div className="flex gap-[3px] mt-3">{Array.from({ length: 12 }, (_, i) => <i key={i} className="h-1.5 flex-1 rounded-[1px]" style={{ background: i < on ? color : 'rgba(138,153,173,0.15)', boxShadow: i < on ? `0 0 6px ${color}66` : 'none' }} />)}</div>;
}

function Tile({ icon: Icon, label, value, sub, st = 'info', ratio, testid }) {
  const col = C[st];
  return (
    <div className="soc-card p-4 min-w-0" data-testid={`tile-${testid}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <span className="soc-mono text-[11px] font-bold uppercase tracking-[0.06em] text-[#CFEFF5] inline-flex items-start gap-1.5 min-w-0 leading-snug"><Icon size={14} style={{ color: C.info }} className="shrink-0 mt-px" /><span>{label}</span></span>
        <span className="soc-led shrink-0 mt-1" style={{ color: col }} />
      </div>
      <p className={`soc-mono ${String(value).length > 8 ? 'text-base' : 'text-2xl'} font-extrabold leading-tight break-words`} style={{ color: st === 'info' ? '#F0F4F8' : col }} data-testid={testid}>{value}</p>
      {sub && <p className="text-[11px] soc-muted mt-1 break-words">{sub}</p>}
      {ratio != null && <Bar ratio={ratio} color={col} />}
    </div>
  );
}

export default function ConsoleKpis({ score, parts = {}, doc, infra, devices, servers }) {
  const own = (infra.sites || []).filter((s) => s.kind === 'own').length;
  const prov = (infra.sites || []).filter((s) => s.kind === 'provider').length;
  const weak = devices.filter((d) => d.issues.length).length;
  const countries = new Set([...servers.map((s) => s.country).filter(Boolean), ...(infra.sites || []).map((s) => s.country_name).filter(Boolean)]);
  const outEu = servers.filter((s) => s.out_eu).length;
  const ep = infra.endpoints || 0;
  return (
    <div className="grid xl:grid-cols-[260px_1fr] gap-4" data-testid="console-kpis">
      <div className="soc-card p-5 flex flex-col items-center justify-center text-center">
        <p className="soc-title mb-2">Indice di sicurezza</p>
        <Gauge value={score} testid="console-score-gauge" />
        <p className="text-[11px] soc-muted mt-1">{score == null ? 'Aggiungi un dominio o un report VA/PT' : `${scoreLabel(score)} · domini ${parts.domains ?? '--'} · VA/PT ${parts.vapt ?? '--'}`}</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile icon={ShieldCheck} label="Esito NIS2" value={doc ? doc.result_label : 'Da compilare'} st={doc ? NIS[doc.result] : 'off'} testid="console-nis2" />
        <Tile icon={Users} label="Utenti" value={infra.users ?? '--'} sub="dichiarati" testid="console-users" />
        <Tile icon={Laptop} label="Endpoint" value={infra.endpoints ?? '--'} sub={`${devices.length} censiti nel registro`} ratio={ep ? devices.length / ep : 0} testid="console-endpoints" />
        <Tile icon={AlertTriangle} label="Device vulnerabili" value={weak} st={weak ? 'bad' : 'ok'} sub={`su ${devices.length} censiti`} ratio={devices.length ? weak / devices.length : 0} testid="console-weak" />
        <Tile icon={Server} label="Server propri" value={infra.servers ?? own} sub={plural(own, 'sede indicata', 'sedi indicate')} testid="console-own" />
        <Tile icon={Cloud} label="Server fornitori" value={prov} sub="hosting, cloud, posta, backup" testid="console-providers" />
        <Tile icon={MapPinned} label="Paesi dei server" value={countries.size} sub={[...countries].slice(0, 3).join(', ') || 'nessun dato'} testid="console-countries" />
        <Tile icon={Globe2} label="Server pubblici fuori UE" value={outEu} st={outEu ? 'warn' : 'ok'} sub={`su ${servers.length} rilevati`} ratio={servers.length ? outEu / servers.length : 0} testid="console-outeu" />
      </div>
    </div>
  );
}
